import { useRecoilValue } from 'recoil';
import { authState } from '../store/auth';
import { userState } from '../store/user';
import { livePreviewState } from '../store/live-preview';

// The platform shell this portal is embedded under. Set per environment; the
// fallback is the local platform dev server.
const PLATFORM_URL =
  process.env.REACT_APP_PLATFORM_URL || 'http://localhost:5173';

export interface HandoffSession {
  token?: string | null;
  refreshToken?: string | null;
  user: unknown;
  isLivePreview?: boolean;
}

// The platform runs on its own origin, so it cannot read this one's localStorage. Only
// what it cannot fetch for itself travels: the token, and the user object — there is no
// endpoint that returns the current user, so `role` has to come across. Company data it
// fetches with the token.
//
// A fragment rather than a query string: it never reaches a server, keeping the token
// out of access logs and Referer headers.
export const platformHandoffUrl = (session: HandoffSession | null) => {
  if (!session?.token) return PLATFORM_URL;
  try {
    return `${PLATFORM_URL}#s=${btoa(encodeURIComponent(JSON.stringify(session)))}`;
  } catch {
    // Never block the navigation over this; the platform will ask them to sign in.
    return PLATFORM_URL;
  }
};

/**
 * Leaves for the platform's app launcher, carrying the session across.
 *
 * A full navigation, not a router push: the platform is a separate app on its own origin.
 *
 * Takes the session explicitly, because the caller that needs it most — the login page —
 * has just received a token that is not in recoil yet. Reading the store there would send
 * the *previous* (empty) session and land the visitor back on the sign-in screen.
 */
export const goToPlatform = (session: HandoffSession | null) => {
  window.location.href = platformHandoffUrl(session);
};

/**
 * The same navigation for callers whose session is already in the store — the "All apps"
 * button and the WinFi mark beside it — so no control can drift on what it sends or where.
 */
export const usePlatformHandoff = () => {
  const auth = useRecoilValue(authState);
  const user = useRecoilValue(userState);
  const livePreview = useRecoilValue(livePreviewState);

  return () =>
    goToPlatform(
      auth.token
        ? {
            token: auth.token,
            refreshToken: auth.refreshToken,
            user,
            // Carried so the platform can mark an impersonated session on screen.
            isLivePreview: livePreview === 'true',
          }
        : null,
    );
};
